<template>
  <v-dialog
    :value="dialog"
    v-if="editedContact"
    :max-width="600"
    persistent
    :retain-focus="false"
    scrollable
    ref="dialog"
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
    @keydown.enter="submit"
  >
    <v-card outlined>
      <v-card-title>
        {{ dialogTitle }}
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          tabindex="4"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text
        v-if="editedContact"
        class="p-contact-edit__card-text"
      >
        <v-form ref="form" :disabled="!hasAccess()" @submit.stop.prevent>
          <div class="p-contact-edit__row">
            <div class="p-contact-edit__icon-col">
              <v-icon>mdi-account-outline</v-icon>
            </div>

            <div class="p-contact-edit__fields-col">
              <v-row>
                <v-col sm="6">
                  <v-text-field
                    type="text"
                    v-model.trim="editedContact.firstName"
                    :label="$t('contact.edit.firstName')"
                    :rules="rules.firstName"
                    hide-details="auto"
                    tabindex="1"
                    clearable
                    autofocus
                    @input="triggerFormValidation"
                  ></v-text-field>
                </v-col>

                <v-col sm="6">
                  <v-text-field
                    type="text"
                    v-model.trim="editedContact.lastName"
                    :label="$t('contact.edit.lastName')"
                    :rules="rules.lastName"
                    hide-details="auto"
                    tabindex="1"
                    clearable
                    @input="triggerFormValidation"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </div>

          <div class="p-contact-edit__row">
            <div class="p-contact-edit__icon-col">
            </div>

            <div class="p-contact-edit__fields-col">
              <v-row>
                <v-col sm="6">
                  <v-text-field
                    type="text"
                    v-model.trim="editedContact.bankAccount"
                    :label="$t('contact.edit.bankAccount')"
                    :rules="rules.bankAccount"
                    hide-details="auto"
                    tabindex="1"
                    clearable
                    validate-on-blur
                  ></v-text-field>
                </v-col>
                <v-col sm="6">
                  <v-text-field
                    type="text"
                    v-model.trim="editedContact.socialSecurityNumber"
                    :label="$t('contact.edit.socialSecurityNumber')"
                    :rules="rules.socialSecurityNumber"
                    hide-details="auto"
                    tabindex="1"
                    clearable
                    validate-on-blur
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </div>

          <div class="p-contact-edit__row">
            <div class="p-contact-edit__icon-col">
              <v-icon>mdi-domain</v-icon>
            </div>

            <div class="p-contact-edit__fields-col">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    type="text"
                    v-model.trim="editedContact.company"
                    :label="$t('contact.edit.company')"
                    :rules="rules.company"
                    hide-details="auto"
                    tabindex="1"
                    clearable
                    @input="triggerFormValidation"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </div>

          <div class="p-contact-edit__row">
            <div class="p-contact-edit__icon-col"></div>

            <div class="p-contact-edit__fields-col">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    type="text"
                    v-model.trim="editedContact.organisationNumber"
                    :label="$t('contact.edit.organisationNumber')"
                    :rules="rules.organisationNumber"
                    hide-details="auto"
                    tabindex="1"
                    clearable
                    validate-on-blur
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </div>

          <div class="p-contact-edit__row">
            <div class="p-contact-edit__icon-col">
              <v-icon>mdi-email-outline</v-icon>
            </div>

            <div class="p-contact-edit__fields-col">
              <v-radio-group
                v-model="emailPrimaryIndex"
                @change="onEmailPrimaryIndexChange"
              >
                <v-row>
                  <span class="p-primary-label">{{ $t('contact.edit.primaryLabel') }}</span>
                </v-row>
                <v-row v-for="(email, index) in emails" :key="`email-${index}`">
                  <v-col cols="7">
                    <v-text-field
                      type="text"
                      v-model.trim="email.email"
                      :label="$t('contact.edit.email')"
                      :hint="false && existingEmails.includes(email.email) ? $t('contact.emailExists') : ''"
                      :class="{'p-text-field-warning-hint': false && existingEmails.includes(email.email)}"
                      hide-details="auto"
                      tabindex="1"
                      clearable
                      validate-on-blur
                      @input="onEmailInput"
                      @blur="onEmailBlur"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-select
                      v-model="email.emailType"
                      :items="emailTypes"
                      :placeholder="$t('contact.edit.emailType')"
                      hide-details="auto"
                      item-text="name"
                      item-value="id"
                      tabindex="1"
                      return-object
                      @change="onEmailTypeChange"
                    ></v-select>
                  </v-col>
                  <v-col cols="2" class="d-flex justify-center align-center pt-4">
                    <v-radio
                      color="primary"
                      :value="index"
                      :disabled="!email.email"
                    ></v-radio>
                  </v-col>
                </v-row>
              </v-radio-group>
            </div>
          </div>

          <div class="p-contact-edit__row">
            <div class="p-contact-edit__icon-col">
              <v-icon>mdi-phone-outline</v-icon>
            </div>

            <div class="p-contact-edit__fields-col">
              <v-radio-group
                v-model="phonePrimaryIndex"
                @change="onPhonePrimaryIndexChange"
              >
                <v-row>
                  <span class="p-primary-label">{{ $t('contact.edit.primaryLabel') }}</span>
                </v-row>
                <v-row v-for="(phone, index) in phones" :key="`phone-${index}`">
                <v-col cols="7">
                  <v-text-field
                    type="text"
                    v-model.trim="phone.phone"
                    :label="$t('contact.edit.phone')"
                    hide-details="auto"
                    tabindex="1"
                    clearable
                    @input="onPhoneInput"
                    @blur="onPhoneBlur"
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-select
                    v-model="phone.phoneType"
                    :items="phoneTypes"
                    :placeholder="$t('contact.edit.phoneType')"
                    hide-details="auto"
                    item-text="name"
                    item-value="id"
                    tabindex="1"
                    return-object
                    @change="onPhoneTypeChange"
                  ></v-select>
                </v-col>
                <v-col cols="2" class="d-flex justify-center align-center pt-4">
                  <v-radio
                    color="primary"
                    :value="index"
                    :disabled="!phone.phone"
                  ></v-radio>
                </v-col>
              </v-row>
              </v-radio-group>
            </div>
          </div>

          <v-expand-transition>
            <div v-show="contentExpanded" class="mt-8">
              <div class="p-contact-edit__row">
                <div class="p-contact-edit__icon-col">
                  <v-icon>mdi-label-outline</v-icon>
                </div>

                <div class="p-contact-edit__fields-col">
                  <v-row>
                    <v-col cols="12">
                      <p-combobox
                        ref="labels"
                        v-model="editedContact.labels"
                        :items="contactLabels"
                        :label="$t('contact.edit.labels')"
                        item-value="id"
                        item-text="name"
                        tabindex="1"
                        clearable
                        multiple
                        small-chips
                        deletable-chips
                        hide-selected
                        @keypress.enter.stop
                      ></p-combobox>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <div class="p-contact-edit__row">
                <div class="p-contact-edit__icon-col">
                  <v-icon>mdi-note-outline</v-icon>
                </div>

                <div class="p-contact-edit__fields-col">
                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model.trim="editedContact.note"
                        :label="$t('contact.edit.note')"
                        hide-details="auto"
                        rows="1"
                        ref="note"
                        tabindex="1"
                        clearable
                        auto-grow
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <div class="p-contact-edit__row">
                <div class="p-contact-edit__icon-col">
                  <v-icon>mdi-card-account-phone-outline</v-icon>
                </div>

                <div class="p-contact-edit__fields-col">
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        type="text"
                        v-model.trim="editedContact.iceName"
                        :label="$t('contact.edit.iceName')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        type="text"
                        v-model.trim="editedContact.icePhone"
                        :label="$t('contact.edit.icePhone')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <div class="p-contact-edit__row">
                <div class="p-contact-edit__icon-col">
                  <v-icon>mdi-gift-outline</v-icon>
                </div>

                <div class="p-contact-edit__fields-col">
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        type="text"
                        v-model.trim="editedContact.bonusProgram"
                        :label="$t('contact.edit.bonusProgram')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <div class="p-contact-edit__row">
                <div class="p-contact-edit__icon-col">
                  <v-icon>mdi-map-marker-outline</v-icon>
                </div>

                <div class="p-contact-edit__fields-col">
                  <v-row>
                    <v-col cols="8">
                      <v-select
                        v-model="editedContact.address.country"
                        :items="countries"
                        :label="$t('contact.edit.country')"
                        hide-details="auto"
                        item-text="name"
                        item-value="id"
                        tabindex="1"
                        clearable
                        return-object
                      ></v-select>
                    </v-col>
                    <v-col cols="4">
                      <v-select
                        v-model="editedContact.address.addressType"
                        :items="addressTypes"
                        :placeholder="$t('contact.edit.addressType')"
                        hide-details="auto"
                        item-text="name"
                        item-value="id"
                        tabindex="1"
                        clearable
                        return-object
                      ></v-select>
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        type="text"
                        v-model.trim="editedContact.address.addressline1"
                        :label="$t('contact.edit.addressLine1')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        type="text"
                        v-model.trim="editedContact.address.addressline2"
                        :label="$t('contact.edit.addressLine2')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        type="text"
                        v-model.trim="editedContact.address.city"
                        :label="$t('contact.edit.city')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        type="text"
                        v-model.trim="editedContact.address.stateProvinceRegion"
                        :label="$t('contact.edit.stateProvinceRegion')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        type="text"
                        v-model.trim="editedContact.address.zipCode"
                        :label="$t('contact.edit.zipCode')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
            </div>

              <div class="p-contact-edit__row mt-8">
                <div class="p-contact-edit__icon-col">
                  <v-icon>mdi-key-outline</v-icon>
                </div>

                <div class="p-contact-edit__fields-col">
                  <v-row>
                    <v-col cols="9">
                      <v-text-field
                        v-model.trim="newEncryptionPassword"
                        :type="isEncryptedPasswordVisible ? 'text' : 'password'"
                        @click:append="() => (isEncryptedPasswordVisible = !isEncryptedPasswordVisible)"
                        :append-icon="isEncryptedPasswordVisible ? 'mdi-eye' : 'mdi-eye-off'"
                        :label="editedContact.isEncryptedPassword ? $t('contact.edit.changeEncryptionPassword') : $t('contact.edit.setEncryptionPassword')"
                        hide-details="auto"
                        tabindex="1"
                        clearable
                        autocomplete="off"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="3">
                      <v-btn
                        class="p-btn-rounded mt-4"
                        text
                        rounded
                        v-if="newEncryptionPassword && editedContact.id"
                        @click.stop="_putEncryptionPassword"
                      >
                        {{ $t('save') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </v-expand-transition>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>

      <v-card-actions class="justify-space-between">
        <v-btn text
               small
               color="grey"
               tabindex="1"
               @keydown.enter.stop.prevent="onExpandButtonPress"
               @click="onExpandButtonPress">
          {{ contentExpanded ? $t('contact.edit.showLess') : $t('contact.edit.showMore') }}
          <v-icon right>{{ contentExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
        <div>
          <v-btn text tabindex="3" @click="closeDialog">{{ $t('cancel') }}</v-btn>
          <v-btn v-if="hasAccess()" text color="primary" tabindex="2" :disabled="!isModelChanged()" @click="submit">{{ $t('save') }}</v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import { contactSchema } from '@/constants'

import PBtnIcon from '@/components/PBtnIcon'
import PCombobox from '@/components/PCombobox'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import errorMixin from '@/mixins/error.mixin'
import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    PBtnIcon,
    PCombobox
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin,
    errorMixin,
    accessMixin
  ],


  props: {
    contact: Object
  },


  data () {
    return {
      editedContact: null,
      contentExpanded: false,
      existingEmails: [],
      validateDisplayName: false,
      phonePrimaryIndex: 0,
      emailPrimaryIndex: 0,
      rule: {
        displayNameRequired: message => {
          return value => {
            message = message || ' '
            const isValid = !!this.editedContact.firstName || !!this.editedContact.lastName || !!this.editedContact.company
            return !this.validateDisplayName || isValid || message
          }
        }
      },
      emails: [],
      newEmail: {},
      phones: [],
      newPhone: {},
      newEncryptionPassword: '',
      isEncryptedPasswordVisible: false
    }
  },


  computed: {
    ...mapState('contact', ['addressTypes', 'phoneTypes', 'emailTypes', 'shortContacts']),
    ...mapState('country', ['countries']),
    ...mapGetters('contactLabel', ['contactLabels', 'contactLabelById']),

    dialogTitle () {
      if (!this.hasAccess()) {
        return this.$t('contact.viewContact')
      }
      return this.contact ? this.$t('contact.editContact') : this.$t('contact.createContact')
    },

    defaultContact () {
      return {
        firstName: '',
        lastName: '',
        bankAccount: '',
        socialSecurityNumber: '',
        company: '',
        organisationNumber: '',
        address: {
          id: '',
          addressline1: '',
          addressline2: '',
          city: '',
          stateProvinceRegion: '',
          zipCode: '',
          country: this.countries[0],
          isPrimary: true,
          addressType: this.addressTypes[0]
        },
        emails: [{
          id: '',
          email: '',
          isPrimary: true,
          emailType: this.emailTypes[0]
        }],
        phones: [{
          id: '',
          phone: '',
          isPrimary: true,
          phoneType: this.phoneTypes[0]
        }],
        labels: [],
        note: '',
        isActive: true
      }
    },

    defaultNewPhone () {
      return {
        id: '',
        phone: '',
        isPrimary: false,
        phoneType: this.phoneTypes[0]
      }
    },

    defaultNewEmail () {
      return {
        id: '',
        email: '',
        isPrimary: false,
        emailType: this.emailTypes[0]
      }
    },

    rulesToApply () {
      const rule = this.rule

      return {
        firstName: [rule.displayNameRequired()],
        lastName: [rule.displayNameRequired()],
        bankAccount: [rule.maxLength(contactSchema.bankAccountMaxLength)],
        socialSecurityNumber: [rule.maxLength(contactSchema.socialSecurityNumberMaxLength)],
        company: [rule.displayNameRequired(this.$t('validation.displayNameRequired'))],
        organisationNumber: [rule.maxLength(contactSchema.organisationNumberMaxLength)],
        email: [rule.email()]
      }
    }
  },


  methods: {
    ...mapActions('contact', ['createContact', 'updateContact', 'loadAddressTypes', 'loadEmailTypes', 'loadPhoneTypes', 'checkUniqueEmail', 'putEncryptionPassword']),
    ...mapActions('country', ['loadCountries']),
    ...mapActions('contactLabel', ['loadContactLabels']),
    ...mapActions('changes', ['removeFromChanges']),

    async _putEncryptionPassword () {
      const payload = {
        id: this.editedContact.id,
        password: this.newEncryptionPassword
      }
      const result = await this.putEncryptionPassword(payload)
      this.newEncryptionPassword = ''
      this.editedContact.isEncryptedPassword = result.isEncryptedPassword
      this.showSnackbarSuccess(this.$t('contact.edit.encryptionPasswordSet'))
    },

    onPhonePrimaryIndexChange () {
      this.updateEditedContactPhones()
    },

    onPhoneTypeChange () {
      this.updateEditedContactPhones()
    },

    onPhoneInput (val) {
      if (val) {
        if (val.length === 1) {
          this.phones.push(this.getObjectCopy(this.defaultNewPhone))
        }
      } else {
        this.phones.forEach((phone, i) => {
          if (this.phones.length > 1 && i < this.phones.length - 1 && !phone.phone) {
            this.phones.splice(i, 1)
          }
        })
      }
      this.setPrimaryPhone()
      this.updateEditedContactPhones()
    },

    onPhoneBlur (el) {
      this.phones.forEach((phone, i) => {
        if (this.phones.length > 1 && i < this.phones.length - 1 && !phone.phone) {
          this.phones.splice(i, 1)
        }
      })

      this.setPrimaryPhone()

      this.updateEditedContactPhones()
    },

    setPrimaryPhone () {
      const phonePrimaryIndex = this.phones.findIndex(phone => phone.isPrimary)
      if (phonePrimaryIndex !== -1) {
        this.phonePrimaryIndex = phonePrimaryIndex
      } else {
        this.phonePrimaryIndex = 0
        this.phones[0].isPrimary = true
      }
    },

    onEmailPrimaryIndexChange () {
      this.updateEditedContactEmails()
    },

    onEmailTypeChange () {
      this.updateEditedContactEmails()
    },

    async checkEmailExists (email) {
      if (this.existingEmails.includes(email)) {
        this.$forceUpdate()
      } else if (this.validateEmail(email)) {
        const result = await this.checkUniqueEmail(email)
        if (!result.uniqueEmail) {
          this.existingEmails.push(email)
          this.$forceUpdate()
        }
      }
    },

    async onEmailInput (val) {
      if (val) {
        if (val.length === 1) {
          this.emails.push(this.getObjectCopy(this.defaultNewEmail))
        }
        // todo uncomment if we need unique email in contacts
        // await this.checkEmailExists(val)
      } else {
        this.emails.forEach((email, i) => {
          if (this.emails.length > 1 && i < this.emails.length - 1 && !email.email) {
            this.emails.splice(i, 1)
          }
        })
      }
      this.setPrimaryEmail()
      this.updateEditedContactEmails()
    },

    async onEmailBlur () {
      this.emails.forEach((email, i) => {
        if (this.emails.length > 1 && i < this.emails.length - 1 && !email.email) {
          this.emails.splice(i, 1)
        }
      })

      this.updateEditedContactEmails()
    },

    setPrimaryEmail () {
      const emailPrimaryIndex = this.emails.findIndex(email => email.isPrimary)
      if (emailPrimaryIndex !== -1) {
        this.emailPrimaryIndex = emailPrimaryIndex
      } else {
        this.emailPrimaryIndex = 0
        this.emails[0].isPrimary = true
      }
    },

    onDialogOpen () {
      if (this.contact) {
        this.removeFromChanges(this.contact)
      }
      this.phones = []
      if (this.contact?.phones?.length) {
        this.phones.push(...this.contact.phones)
      }
      const phonePrimaryIndex = this.phones.findIndex(phone => phone.isPrimary)
      if (phonePrimaryIndex !== -1) {
        this.phonePrimaryIndex = phonePrimaryIndex
      }
      this.phones.push(this.getObjectCopy(this.defaultNewPhone))
      this.newPhone = this.getObjectCopy(this.defaultNewPhone)
      this.setPrimaryPhone()

      this.emails = []
      if (this.contact?.emails?.length) {
        this.emails.push(...this.contact.emails)
      }

      // todo uncomment if we need unique contact emails
      // this.existingEmails = []
      // if (this.contact?.emails?.length) {
      //   this.existingEmails.push(...this.contact.emails.map(email => email.email))
      // }

      this.emails.push(this.getObjectCopy(this.defaultNewEmail))
      this.newEmail = this.getObjectCopy(this.defaultNewEmail)
      this.setPrimaryEmail()

      this.validateDisplayName = false
      this.contentExpanded = false
      this.editedContact = this.getObjectCopy(this.contact || this.defaultContact)

      this.editedContact.labels = this.editedContact.labels.map(label => {
        return this.contactLabelById(label)
      })

      if (!this.editedContact.address) this.editedContact.address = Object.assign({}, this.defaultContact.address)
      if (!this.editedContact.emails?.length) this.editedContact.emails = Object.assign({}, this.defaultContact.emails)
      if (!this.editedContact.phones?.length) this.editedContact.phones = Object.assign({}, this.defaultContact.phones)

      this.initWatchedModel(this.editedContact)
      this.resetValidation()
    },

    async onExpandButtonPress () {
      this.contentExpanded = !this.contentExpanded
      if (this.contentExpanded) {
        await this.$nextTick(() => {
          this.$refs.labels.$refs.combobox.focus()
        })
      }
    },

    triggerFormValidation () {
      this.validate(this.rulesToApply)
    },

    updateEditedContactEmails () {
      if (this.emails.length === 1 && !this.emails[0].email) {
        this.editedContact.emails = null
      } else {
        this.editedContact.emails = []
        this.emails.forEach(email => {
          if (email.email) {
            if (!email.id) {
              delete email.id
            }
            email.isPrimary = false
            this.editedContact.emails.push(email)
          }
        })
        this.editedContact.emails[this.emailPrimaryIndex].isPrimary = true
      }
    },

    updateEditedContactPhones () {
      if (this.phones.length === 1 && !this.phones[0].phone) {
        this.editedContact.phones = null
      } else {
        this.editedContact.phones = []
        this.phones.forEach(phone => {
          if (phone.phone) {
            if (!phone.id) {
              delete phone.id
            }
            phone.isPrimary = false
            this.editedContact.phones.push(phone)
          }
        })
        this.editedContact.phones[this.phonePrimaryIndex].isPrimary = true
      }
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return
      this.validateDisplayName = true

      try {
        this.showSnackbarProcessing()
        const contact = this.getObjectCopy(this.editedContact)

        if (this.emails.length === 1 && !this.emails[0].email) {
          contact.emails = null
        } else {
          contact.emails = []
          this.emails.forEach(email => {
            if (email.email) {
              if (!email.id) {
                delete email.id
              }
              email.isPrimary = false
              contact.emails.push(email)
            }
          })
          contact.emails[this.emailPrimaryIndex].isPrimary = true
        }

        if (this.phones.length === 1 && !this.phones[0].phone) {
          contact.phones = null
        } else {
          contact.phones = []
          this.phones.forEach(phone => {
            if (phone.phone) {
              if (!phone.id) {
                delete phone.id
              }
              phone.isPrimary = false
              contact.phones.push(phone)
            }
          })
          contact.phones[this.phonePrimaryIndex].isPrimary = true
        }

        if (!contact.address.addressline1 &&
            !contact.address.addressline2 &&
            !contact.address.country &&
            !contact.address.city &&
            !contact.address.stateProvinceRegion &&
            !contact.address.zipCode) {
          contact.address = null
        }

        const labels = []
        for (const label of contact.labels) {
          if (typeof label === 'string') {
            labels.push({ name: label })
          } else {
            labels.push(label)
          }
        }
        contact.labels = labels

        if (contact.id) {
          await this.updateContact(contact)
          this.showSnackbarSuccess(this.$t('contact.contactUpdated'))
          if (this.newEncryptionPassword) {
            this._putEncryptionPassword()
          }
        } else {
          const addedContact = await this.createContact(contact)
          this.showSnackbarSuccess(this.$t('contact.contactCreated'))

          this.editedContact = addedContact
          if (this.newEncryptionPassword) {
            this._putEncryptionPassword()
          }
          this.$emit('add', addedContact)
          this.loadContactLabels()
        }
        this.$parent.closeViewDialog()
        this.closeDialog()
        this.validateDisplayName = false
      } catch (error) {
        if (this.is409ConflictError(error)) {
          this.hideSnackbar()
          this.existingEmails.push(this.editedContact.email.email)
          this.validate(this.rulesToApply)
        } else {
          this.showSnackbarError()
        }
        this.$log.error(error)
      }
    }
  },


  created () {
    try {
      this.loadAddressTypes()
      this.loadEmailTypes()
      this.loadPhoneTypes()
      this.loadCountries()
    } catch (error) {
      this.$log.error(error)
    }
  }
}
</script>
